import { combineReducers, configureStore } from "@reduxjs/toolkit";
import type { ReactNode } from "react";
import { Provider, useDispatch } from "react-redux";
import {
  FLUSH,
  PAUSE,
  PERSIST,
  persistReducer,
  persistStore,
  PURGE,
  REGISTER,
  REHYDRATE,
} from "redux-persist";
import { PersistGate } from "redux-persist/integration/react";
import storage from "redux-persist/lib/storage";

import { daoApi } from "~/api/Dao/daoApi";
import { inventoryApi } from "~/api/Inventory/inventoryApi";
import { marketApi } from "~/api/Market/marketApi";
import { memberApi } from "~/api/Member/memberApi";
import channelChatMessageReducer from "./chat/channelChatMessageSlice";
import channelThreadMessageReducer from "./chat/channelThreadMessageSlice";
import dmMessageReducer from "./chat/dmMessageSlice";
import marketNftMessageReducer from "./chat/marketNftMessageSlice";

const persistConfig = {
  key: "root",
  storage,
  whitelist: ["createCommunity"],
};
const rootReducer = combineReducers({
  // chat
  channelChatMessage: channelChatMessageReducer,
  dmMessage: dmMessageReducer,
  marketNftMessage: marketNftMessageReducer,
  channelThreadMessage: channelThreadMessageReducer,
  //
  [inventoryApi.reducerPath]: inventoryApi.reducer,
  [marketApi.reducerPath]: marketApi.reducer,
  [daoApi.reducerPath]: daoApi.reducer,
  [memberApi.reducerPath]: memberApi.reducer,
});
const persistedReducer = persistReducer(persistConfig, rootReducer);

export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }).concat(
      marketApi.middleware,
      inventoryApi.middleware,
      memberApi.middleware,
      daoApi.middleware,
      // logger,
    ),
});

const persist = persistStore(store);

export type RootState = ReturnType<typeof store.getState>;

function StoreProvider(params: { children: ReactNode }) {
  return (
    <Provider store={store}>
      <PersistGate persistor={persist}>{params.children}</PersistGate>
    </Provider>
  );
}

export type AppDispatch = typeof store.dispatch;
export const useAppDispatch: () => AppDispatch = useDispatch;

export default StoreProvider;
